import React, {useState} from "react";
import "./style.scss";
import Close from "../../../assets/icons/circle-cross.svg";
import Select from "react-select";
import ApartmentNoIcon from "../../../assets/icons/apartment-no.svg";
import StreetI from "../../../assets/icons/street.svg";
import StateIcon from "../../../assets/icons/state.svg";
import ZipCodeIcon from "../../../assets/icons/zip-code.svg";
import CountryIcon from "../../../assets/icons/country.svg";
import {UpdateUserAddress, getUserAddress} from "../../../utils/apis";
import {useDispatch, useSelector} from "react-redux";
import {country} from "../../../utils/constants/country";
import {returnErrors} from "../../../store/reducers/error";
import {returnMessages} from "../../../store/reducers/message";
import customStyles, {cryptoNewCustomStyles, newCustomStyles} from "../../../customstyle/customstyle";
import {getAddressConfig} from "../../../utils/helpers/addressFormat";

function EditAddressModal({closeEditAddressModal, addressId, data, setUserAddress, paymentMethod}) {
  const dispatch = useDispatch();
  const idToken = useSelector((state) => state.auth.idToken);
  const selectedBrand = useSelector((state) => state.brand.selectedBrand);
  const isPaymid = paymentMethod?.value?.toLowerCase() === "paymid";
  const apartmentLabel = isPaymid ? "House No" : "Apartment No";
  const [inputValues, setInputValues] = useState({
    id: addressId,
    apartment_no: data?.apartment_no || data?.house_number || "",
    city: data?.city || "",
    country: data?.country || "",
    default: false,
    state: data?.state || "",
    street_address: data?.street_address || "",
    zip_code: data?.zip_code || "",
    profile: data?.profile || "",
  });

  const addressConfig = getAddressConfig(inputValues.country);

  const [inputErrors, setInputErrors] = useState({
    apartment_no: "",
    street_address: "",
    city: "",
    state: "",
    zip_code: "",
    country: "",
    general: "",
  });

  const [focusedInput, setFocusedInput] = useState(null);

  const stateOptions = [{value: "ca", label: "California"}];

  const countryOptions = [{value: "ca", label: "Canada"}];

  const handleInputChange = (e) => {
    const {name, value} = e.target;
    setFocusedInput(name);

    if (name === "zip_code") {
      const sanitized = addressConfig.sanitizePostal(value);
      setInputValues((prev) => ({...prev, [name]: sanitized}));
      setInputErrors((prev) => ({...prev, zip_code: ""}));
    } else if (name === "state") {
      setInputValues((prev) => ({...prev, [name]: value}));
      setInputErrors((prev) => ({...prev, state: ""}));
    } else {
      setInputValues((prev) => ({...prev, [name]: value}));
      setInputErrors((prev) => ({...prev, [name]: ""}));
    }
  };

  const handlePostalBlur = () => {
    handleInputBlur();
    if (!inputValues.zip_code?.toString()?.trim()) {
      if (addressConfig.isPostalRequired) {
        setInputErrors((prev) => ({
          ...prev,
          zip_code: `Please fill in the ${addressConfig.postalLabel.toLowerCase()} field`,
        }));
      }
      return;
    }
    const formatted = addressConfig.formatPostal(inputValues.zip_code);
    setInputValues((prev) => ({...prev, zip_code: formatted}));
    const errorMsg = addressConfig.validatePostal(formatted);
    setInputErrors((prev) => ({...prev, zip_code: errorMsg}));
  };

  const handleStateBlur = () => {
    handleInputBlur();
    const errorMsg = addressConfig.validateState(inputValues.state);
    setInputErrors((prev) => ({...prev, state: errorMsg}));
  };

  const handleSelectChange = (name, selectedOption) => {
    setInputValues((prev) => ({...prev, [name]: selectedOption}));
    setInputErrors((prev) => ({...prev, [name]: ""}));

    if (name === "country") {
      const nextConfig = getAddressConfig(selectedOption);
      setInputErrors((prev) => ({...prev, zip_code: "", state: ""}));
      if (inputValues.zip_code) {
        const formatted = nextConfig.formatPostal(inputValues.zip_code);
        setInputValues((prev) => ({...prev, zip_code: formatted}));
      }
    }
  };

  const handleInputFocus = (name) => {
    setFocusedInput(name);
  };

  const handleInputBlur = () => {
    setFocusedInput(null);
  };

  const handleSubmit = async (e) => {
    if (e?.preventDefault) {
      e.preventDefault();
    }
    const errors = {};

    // Check for empty fields
    if (!inputValues.apartment_no?.trim()) errors.apartment_no = `Please fill in the ${isPaymid ? "house number" : "apartment number"} field`;
    if (!inputValues.street_address?.trim()) errors.street_address = "Please fill in the street address field";
    if (!inputValues.country) errors.country = "Please select a country";
    if (!inputValues.city?.trim()) errors.city = "Please fill in the city field";

    const stateErr = addressConfig.validateState(inputValues.state);
    if (stateErr) errors.state = stateErr;

    const formattedZip = addressConfig.formatPostal(inputValues.zip_code || "");
    const postalErr = addressConfig.validatePostal(formattedZip);
    if (postalErr) errors.zip_code = postalErr;

    setInputErrors(errors);

    if (Object.keys(errors).length > 0) {
      dispatch(returnErrors(Object.values(errors)[0], 400));
      return;
    }

    try {
      const payload = { ...inputValues, zip_code: formattedZip };
      const response = await UpdateUserAddress(idToken, payload);

      if (response && response.status === 200) {
        dispatch(returnMessages("Successfully Updated Address", 200));
      }

      const data = await getUserAddress(idToken);
      setUserAddress(data);
      closeEditAddressModal();
    } catch (error) {
      dispatch(returnErrors(error?.response?.data?.detail || "Error updating address", error?.response?.status || 500));
    }
  };

  return (
    <div className="edit_address_modal_container">
      <div className="modal_header">
        <h4>Edit New Address</h4>
        <img
          src={Close}
          alt="close icon"
          onClick={closeEditAddressModal}
        />
      </div>
      <div className="address_form_wrapper">
        <div className="edit_address_info_container">
          <div className="billing_info_form_container">
            <p>Billing Address</p>
            <div className="billing_info_form">
              <div className="general_input_wrapper">
                <label className="general_label">{apartmentLabel}</label>
                <label className={`address_info_form_input_container ${focusedInput === "apartment_no" ? "focused" : ""} ${inputErrors.apartment_no ? "input_error" : ""}`}>
                  <img
                    src={ApartmentNoIcon}
                    alt="apartment icon"
                    className="input_icon"
                  />

                  <div className="add_address_input general_info_input">
                    <input
                      type="text"
                      name="apartment_no"
                      id="apartment_no"
                      placeholder={isPaymid ? "House no." : "Apartment no."}
                      value={inputValues.apartment_no}
                      onChange={handleInputChange}
                      onFocus={() => handleInputFocus("apartment_no")}
                      onBlur={handleInputBlur}
                    />
                  </div>
                </label>
              </div>

              <div className="general_input_wrapper">
                <label className="general_label">Country</label>
                <label className={`address_info_form_input_container ${focusedInput === "country" ? "focused" : ""} ${inputErrors.country ? "input_error" : ""}`}>
                  <img
                    src={CountryIcon}
                    alt="country"
                    className="input_icon"
                  />
                  <div
                    className="add_address_input general_info_input"
                    style={{paddingLeft: "0"}}
                  >
                    <label
                      className="general_info_label"
                      htmlFor="country"
                    >
                      Country
                    </label>
                    <Select
                      styles={selectedBrand?.brand === "equity_edge" ? newCustomStyles(31) : selectedBrand?.brand === "crypto_edge" ? cryptoNewCustomStyles(31) : newCustomStyles(31)}
                      options={country.map((item) => ({
                        ...item,
                        value: `${item?.country}`,
                        label: `${item?.country}`,
                      }))}
                      components={{
                        IndicatorSeparator: () => null,
                      }}
                      className="react_select_container"
                      classNamePrefix="react-select"
                      name="country"
                      value={{label: inputValues.country, value: inputValues.country}}
                      onChange={(selectedOption) => handleSelectChange("country", selectedOption.label)}
                      onFocus={() => handleInputFocus("country")}
                      onBlur={handleInputBlur}
                    />
                  </div>
                </label>
              </div>
              {/* city */}

              <div className="general_input_wrapper">
                <label className="general_label">City</label>
                <label className={`address_info_form_input_container ${focusedInput === "city" ? "focused" : ""} ${inputErrors.city ? "input_error" : ""}`}>
                  <img
                    src={StreetI}
                    alt="city addrress"
                    className="input_icon"
                  />
                  <div className="add_address_input general_info_input">
                    <label
                      className="general_info_label"
                      htmlFor="city"
                    >
                      City
                    </label>
                    <input
                      type="text"
                      name="city"
                      id="city"
                      value={inputValues.city}
                      onChange={handleInputChange}
                      onFocus={() => handleInputFocus("city")}
                      onBlur={handleInputBlur}
                    />
                  </div>
                </label>
              </div>

              <div className="general_input_wrapper">
                <label className="general_label">Street Address</label>
                <label className={`address_info_form_input_container ${focusedInput === "street_address" ? "focused" : ""} ${inputErrors.street_address ? "input_error" : ""}`}>
                  <img
                    src={StreetI}
                    alt="street_address addrress"
                    className="input_icon"
                  />
                  <div className="add_address_input general_info_input">
                    <label
                      className="general_info_label"
                      htmlFor="street_address"
                    >
                      Street Address
                    </label>
                    <input
                      type="text"
                      name="street_address"
                      id="street_address"
                      value={inputValues.street_address}
                      onChange={handleInputChange}
                      onFocus={() => handleInputFocus("street_address")}
                      onBlur={handleInputBlur}
                    />
                  </div>
                </label>
              </div>

              <div className="general_input_wrapper">
                <label className="general_label">{addressConfig.stateLabel}</label>
                <label className={`address_info_form_input_container ${focusedInput === "state" ? "focused" : ""} ${inputErrors.state ? "input_error" : ""}`}>
                  <img
                    src={StreetI}
                    alt="state addrress"
                    className="input_icon"
                  />
                  <div className="add_address_input general_info_input">
                    <label
                      className="general_info_label"
                      htmlFor="state"
                    >
                      {addressConfig.stateLabel}
                    </label>
                    <input
                      type="text"
                      name="state"
                      id="state"
                      placeholder={addressConfig.statePlaceholder}
                      value={inputValues.state}
                      onChange={handleInputChange}
                      onFocus={() => handleInputFocus("state")}
                      onBlur={handleStateBlur}
                    />
                  </div>
                </label>
                {inputErrors.state && <span className="error_message">{inputErrors.state}</span>}
              </div>

              <div className="general_input_wrapper">
                <label className="general_label">{addressConfig.postalLabel}</label>
                <label className={`address_info_form_input_container ${focusedInput === "zip_code" ? "focused" : ""} ${inputErrors.zip_code ? "input_error" : ""}`}>
                  <img
                    src={ZipCodeIcon}
                    alt="zip code"
                    className="input_icon"
                  />
                  <div className="add_address_input general_info_input">
                    <label
                      className="general_info_label"
                      htmlFor="zip_code"
                    >
                      {addressConfig.postalLabel}
                    </label>
                    <input
                      type="text"
                      name="zip_code"
                      id="zip_code"
                      placeholder={addressConfig.postalPlaceholder}
                      value={inputValues.zip_code}
                      onChange={handleInputChange}
                      onFocus={() => handleInputFocus("zip_code")}
                      onBlur={handlePostalBlur}
                    />
                  </div>
                </label>
                {inputErrors.zip_code && <span className="error_message">{inputErrors.zip_code}</span>}
              </div>
            </div>
          </div>
        </div>
      </div>
      <div className="btn_container">
        <button
          onClick={() => handleSubmit()}
          className="save_info_btn"
        >
          Save Information
        </button>
      </div>
    </div>
  );
}

export default EditAddressModal;
